import { Checkbox, Stack, Text } from '@chakra-ui/react';
import { concat, difference, union, without } from 'lodash';
import { Dispatch, SetStateAction, useEffect } from 'react';

import { AlgoAction, GrowthAllocationType } from '../../../_openapi/portfolio-optimizer';
import { ALLOCATION_TYPES, AllocationType } from '../../allocation_types';

export const AllocationTypeSelector = ({
    selectedAlgoAction,
    selectedAllocationTypes,
    setSelectedAllocationTypes,
}: {
    selectedAlgoAction?: AlgoAction;
    selectedAllocationTypes: AllocationType[];
    setSelectedAllocationTypes: Dispatch<SetStateAction<AllocationType[]>>;
}) => {
    const allAllocationTypes = ALLOCATION_TYPES.flatMap(
        ({ allocationType, children }) => allocationType ?? children.map((child) => child.allocationType),
    ).filter((item) => selectedAlgoAction !== AlgoAction.BUY || item !== GrowthAllocationType.CASH_EQUIVALENTS);

    useEffect(() => {
        if (
            selectedAlgoAction === AlgoAction.BUY &&
            selectedAllocationTypes.length > 1 &&
            selectedAllocationTypes.includes(GrowthAllocationType.CASH_EQUIVALENTS)
        ) {
            setSelectedAllocationTypes((current) =>
                current.filter((item) => item !== GrowthAllocationType.CASH_EQUIVALENTS),
            );
        }
    }, [selectedAlgoAction, selectedAllocationTypes, setSelectedAllocationTypes]);

    const selectAllChecked = allAllocationTypes.every((item) => selectedAllocationTypes.includes(item));
    const selectAllIndeterminate =
        !selectAllChecked && allAllocationTypes.some((item) => selectedAllocationTypes.includes(item));

    return (
        <Stack
            flexShrink={0}
            px={5}
            py={3}
            borderColor="gray.600"
            borderWidth={1}
            borderRadius="md"
            background="gray.800"
            height="fit-content"
        >
            <Text fontWeight="medium">Allocation types</Text>
            <Stack>
                <Checkbox
                    colorScheme="teal"
                    isChecked={selectAllChecked}
                    isIndeterminate={selectAllIndeterminate}
                    onChange={() => setSelectedAllocationTypes(selectAllChecked ? [] : allAllocationTypes)}
                >
                    Select all
                </Checkbox>
                {ALLOCATION_TYPES.map(({ label, allocationType, children }) => {
                    const groupAllocationTypes = allocationType
                        ? [allocationType]
                        : children.map((child) => child.allocationType);

                    const groupChecked = groupAllocationTypes.every((item) => selectedAllocationTypes.includes(item));
                    const groupIndeterminate =
                        !groupChecked && groupAllocationTypes.some((item) => selectedAllocationTypes.includes(item));

                    const disabled =
                        selectedAlgoAction === AlgoAction.BUY &&
                        (allocationType === GrowthAllocationType.CASH_EQUIVALENTS
                            ? selectedAllocationTypes.some((item) => item !== GrowthAllocationType.CASH_EQUIVALENTS)
                            : selectedAllocationTypes.includes(GrowthAllocationType.CASH_EQUIVALENTS));

                    return (
                        <Stack key={label} pl={6} spacing={1}>
                            <Checkbox
                                colorScheme="teal"
                                isChecked={groupChecked}
                                isIndeterminate={groupIndeterminate}
                                isDisabled={disabled}
                                onChange={() =>
                                    setSelectedAllocationTypes((current) =>
                                        groupChecked
                                            ? difference(current, groupAllocationTypes)
                                            : union(current, groupAllocationTypes),
                                    )
                                }
                            >
                                {label}
                            </Checkbox>
                            <Stack pl={6} spacing={1}>
                                {children?.map(({ label: childLabel, allocationType: childAllocationType }) => {
                                    const isChecked = selectedAllocationTypes.includes(childAllocationType);
                                    return (
                                        <Checkbox
                                            key={childAllocationType}
                                            colorScheme="teal"
                                            isChecked={isChecked}
                                            isDisabled={disabled}
                                            onChange={() =>
                                                setSelectedAllocationTypes((current) =>
                                                    isChecked
                                                        ? without(current, childAllocationType)
                                                        : concat(current, childAllocationType),
                                                )
                                            }
                                        >
                                            {childLabel}
                                        </Checkbox>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        </Stack>
    );
};
